
import React, { FC, ReactNode, useMemo } from 'react';
import { ConnectionProvider, WalletProvider } from '@solana/wallet-adapter-react';
import { 
  PhantomWalletAdapter,
  SolflareWalletAdapter,
  TorusWalletAdapter,
  CoinbaseWalletAdapter,
  AlphaWalletAdapter,
} from '@solana/wallet-adapter-wallets';
import { WalletModalProvider } from '@solana/wallet-adapter-react-ui';
import { clusterApiUrl } from '@solana/web3.js';
import { useIsMobile } from '@/hooks/use-mobile';
import { detectDevicePlatform } from '@/utils/wallet-detection';

// Import the wallet adapter styles
import '@solana/wallet-adapter-react-ui/styles.css';

// Define the WalletAdapterNetwork enum locally since it's not exported directly
enum WalletAdapterNetwork {
  Mainnet = 'mainnet-beta',
  Testnet = 'testnet',
  Devnet = 'devnet',
}

interface SolanaWalletProviderProps {
  children: ReactNode;
}

const SolanaWalletProvider: FC<SolanaWalletProviderProps> = ({ children }) => {
  const isMobile = useIsMobile();
  const platform = useMemo(() => detectDevicePlatform(), []);
  
  // Use mainnet for production to connect to real tokens
  const network = WalletAdapterNetwork.Mainnet;
  
  // Set endpoint with fallback - using mainnet for live token prices
  const endpoint = useMemo(() => {
    // Use mainnet-beta for production to work with real tokens
    return "https://api.mainnet-beta.solana.com";
  }, []);

  // Initialize wallet adapters with mobile-friendly options
  const wallets = useMemo(() => {
    // Configure adapters with mobile-specific options when appropriate
    const mobileOptions = { 
      network,
      appIdentity: { 
        name: "Swadez Web3 App",
        icon: "https://i.imgur.com/UjTUfjU.png" // Your app's icon URL
      },
      // Set deep link options for better iOS 16+ and 18+ support
      deepLinkOptions: {
        mobile: {
          usesDeepLinks: true,
          resolveDeepLink: (deepLink: string) => {
            return deepLink;
          }
        }
      }
    };
    
    // Define wallet adapters with enhanced mobile support
    const phantomWallet = new PhantomWalletAdapter(mobileOptions);
    const solflareWallet = new SolflareWalletAdapter(mobileOptions);
    const alphaWallet = new AlphaWalletAdapter({ network }); // Using Alpha instead of Backpack
    const torusWallet = new TorusWalletAdapter();
    const coinbaseWallet = new CoinbaseWalletAdapter();
    
    console.log("Initializing wallet adapters:", { 
      isMobile, 
      isIOS: platform.isIOS,
      isIOS16Plus: platform.isIOS16Plus,
      isIOS18Plus: platform.isIOS18Plus, 
      isAndroid: platform.isAndroid,
      userAgent: navigator.userAgent
    });
    
    return [
      phantomWallet,
      solflareWallet,
      alphaWallet, // Changed from backpackWallet
      torusWallet,
      coinbaseWallet,
    ];
  }, [network, isMobile, platform]);

  console.log("SolanaWalletProvider rendering with endpoint:", endpoint, "isMobile:", isMobile);
  
  return (
    <ConnectionProvider endpoint={endpoint}>
      <WalletProvider wallets={wallets} autoConnect={true}>
        <WalletModalProvider>
          {children}
        </WalletModalProvider>
      </WalletProvider>
    </ConnectionProvider>
  );
};

export default SolanaWalletProvider;
